import {
  Alert,
  Paper,
  Box,
  Typography,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AiNegotiationDialog from "../../components/AiNegotiationDialog";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import useCaseList from "../../hooks/useCaseList";
import { caseActions } from "../../config/action-config";
import {
  addNegotiationDetails,
  getNegotiationDetails,
  confirmNegotiation,
  getNegotiationInsight,
  withdrawNegotiation,
} from "../../api/caseNegotiations";
import {
  EXPIRY_CHIP_CLASS,
  RESPONSE_HOURS_DEFAULT,
  RESPONSE_HOURS_OPTIONS,
  expiryInfo,
  responseHoursLabel,
} from "../../config/negotiation-utils";
import useCustomAlert from "../../hooks/useCustomAlert";
import useSnackbar from "../../hooks/useSnackbar";
import useErrorHandler from "../../hooks/useErrorHandler";
import OfferChart from "./OfferChart";
import ProposalPanel from "./ProposalPanel";
import TermsEditor, { TermsList, readyItems, useTermCatalog } from "./TermsEditor";
import BlindBidPanel from "./BlindBidPanel";
import AiAdviceLog from "./AiAdviceLog";
import {
  BASIS_TEXT,
  SIDE_STYLE,
  buildOfferEntries,
  fmtDateTime,
  toUSD,
} from "./negotiation-ui";

const tempNeg = {
  id: 11,
  title: "Title",
  description: "Description",
  start_amount: 0,
  latest_amount: 0,
  creator_name: "",
  updated_at: new Date().toISOString(),
  status: "CLOSED",
  creator_id: 1,
  case_id: 1,
  created_at: new Date().toISOString(),
};

const INACTIVE = ["CLOSED", "WITHDRAWN", "ENDED"];

export default function NegotiationDetails() {
  const [details, setDetails] = useState([]);
  const [neg, setNeg] = useState(tempNeg);
  const [loading, setLoading] = useState(true);
  const params = useParams();
  const { data: user } = useSelector((state) => state.user);
  const snackbar = useSnackbar();
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const errorHandler = useErrorHandler();
  const { caseList, setSelectedCaseId } = useCaseList();
  // A negotiation belongs to one case, so choosing another case opens that case's negotiations.
  function onCaseChange({ target }) {
    setSelectedCaseId(target.value);
    dispatch({ type: caseActions.SELECT, payload: { id: target.value } });
    navigation(`/negotiation/${target.value}`);
  }
  const [counterNeg, setCounterNeg] = useState({
    neg_id: params.negId,
    amount: "",
    text: "",
    terms: "",
    response_hours: RESPONSE_HOURS_DEFAULT,
  });
  const { catalog, maxItems } = useTermCatalog(params.caseId);
  // structured terms for the next counter-offer; starts from what is on the table so the user adjusts it
  const [counterItems, setCounterItems] = useState([]);
  const itemsDirty = useRef(false);
  const [viewer, setViewer] = useState(null);
  const [settlement, setSettlement] = useState(null);
  const [proposals, setProposals] = useState(null);
  const [blind, setBlind] = useState(null);
  const [hoursOptions, setHoursOptions] = useState(RESPONSE_HOURS_OPTIONS);
  const [amountError, setAmountError] = useState("");
  const [busy, setBusy] = useState(false);
  // seconds_left is a snapshot from the server; tick it down locally every minute
  const fetchedAt = useRef(Date.now());
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiInsight, setAiInsight] = useState(null);
  const listRef = useRef(null);

  function onCounterNegChange({ target }) {
    setCounterNeg((prev) => ({ ...prev, [target.name]: target.value }));
    if (target.name === "amount") setAmountError("");
  }

  async function onCounterNegSubmit(e) {
    e.preventDefault();
    const monetary = neg.monetary !== false;
    const amount = Number(counterNeg.amount);
    if (monetary && !(amount > 0)) {
      setAmountError("Enter an amount greater than $0.");
      return;
    }
    const ready = readyItems(counterItems, catalog);
    if (ready.error) {
      snackbar.showError(ready.error);
      return;
    }
    if (!monetary && !ready.items.length) {
      snackbar.showError("Add at least one term.");
      return;
    }
    const { terms, amount: _unused, ...rest } = counterNeg;
    setBusy(true);
    try {
      await addNegotiationDetails(user.token, params.caseId, {
        ...rest,
        ...(monetary ? { amount } : {}),
        terms_items: ready.items,
        ...(terms.trim() ? { terms: terms.trim() } : {}),
      });
      itemsDirty.current = false;
      setCounterNeg((prev) => ({ ...prev, amount: "", text: "", terms: "" }));
      snackbar.showSuccess("New Offer Posted");
      getNegotiationDetailsData();
    } catch (error) {
      errorHandler(error);
      // turn / expiry may have changed on the server, so resync the controls
      getNegotiationDetailsData();
    } finally {
      setBusy(false);
    }
  }

  async function getNegotiationDetailsData() {
    return getNegotiationDetails(user.token, params.caseId, params.negId)
      .then(({ data }) => {
        const {
          negotiation,
          details,
          viewer,
          settlement,
          proposals,
          blind,
          responseHoursOptions,
        } = data.data;
        fetchedAt.current = Date.now();
        setNow(Date.now());
        setViewer(viewer || null);
        setSettlement(settlement || null);
        setProposals(proposals || null);
        setBlind(blind || null);
        if (responseHoursOptions?.length) setHoursOptions(responseHoursOptions);
        setCounterNeg((prev) => ({
          ...prev,
          response_hours: negotiation.response_hours || RESPONSE_HOURS_DEFAULT,
        }));
        setNeg(negotiation);
        setDetails(details);
      })
      .catch((error) => {
        errorHandler(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    if (!user.token) return;
    getNegotiationDetailsData();
  }, [params.caseId, params.negId, user.token]);

  // pick up what the other side does (an answer to a proposal, a new offer) without a manual reload
  useEffect(() => {
    if (!user.token) return undefined;
    const refresh = () => {
      if (document.visibilityState === "visible") getNegotiationDetailsData();
    };
    const t = setInterval(refresh, 30000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [params.caseId, params.negId, user.token]);

  // keep the newest message in view
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [details.length, loading]);

  const customAlert = useCustomAlert();
  function onConfirmClick() {
    customAlert.info(
      neg.monetary === false ? "Confirm and settle on these terms" : `Confirm and settle for ${toUSD(neg.latest_amount)}`,
      onAgree
    );
  }
  const onAgree = async () => {
    try {
      await confirmNegotiation(user.token, params.caseId, params.negId);
      snackbar.showSuccess("Confirmed");
      getNegotiationDetailsData();
    } catch (error) {
      errorHandler(error);
    }
  };

  const isBlind = neg.mode === "BLIND";
  const monetary = neg.monetary !== false; // false = a negotiation about terms only
  const closed = neg.status === "CLOSED";
  const active = !INACTIVE.includes(neg.status);
  const secondsLeft =
    typeof neg.seconds_left === "number"
      ? neg.seconds_left - (now - fetchedAt.current) / 1000
      : null;
  const expiry = !active || isBlind ? null : expiryInfo(secondsLeft);
  const expiredNow = expiry?.kind === "expired";
  const isTrial = /^trial_user$/i.test(user.roles);
  const isAdmin = /^admin$/i.test(user.roles);
  // after the opening offer is withdrawn, either party can start over with a new offer
  const canRestart = neg.status === "WITHDRAWN" && Boolean(viewer?.restart) && Boolean(viewer?.canOffer) && !isTrial;
  const canOffer = ((active && !isBlind) || canRestart) && Boolean(viewer?.canOffer) && !isTrial;
  // an offer that expired while the page was open can no longer be accepted
  const canAccept =
    active && !isBlind && Boolean(viewer?.canAccept) && !expiredNow && !isTrial;
  const canWithdraw = active && Boolean(viewer?.canWithdraw) && !isTrial;
  const canCounter = canOffer;
  const helperText =
    active && !isBlind && viewer && (!viewer.canAccept || !viewer.canOffer || expiredNow)
      ? expiredNow && viewer.canAccept
        ? "The latest offer has expired. Make a new offer to continue."
        : viewer.reason
      : null;
  const showRound = !isBlind && viewer && viewer.maxRounds != null;

  const entries = buildOfferEntries(neg, details, viewer, user.id);
  const liveEntries = entries.filter((e) => !e.withdrawn_at);
  const latestLive = liveEntries[liveEntries.length - 1];

  // start the next counter-offer from the terms now on the table, unless the user has already started editing them
  useEffect(() => {
    if (!itemsDirty.current) setCounterItems((latestLive?.items || []).map((i) => ({ type: i.type, value: i.value })));
  }, [latestLive?.key]);

  // refresh server-computed permissions once the offer expires while the page is open
  useEffect(() => {
    if (expiredNow && viewer?.canAccept) getNegotiationDetailsData();
  }, [expiredNow]);

  function onWithdrawClick() {
    const whole = latestLive?.opening;
    customAlert.warning(
      whole
        ? "Withdraw your opening offer? Nothing will be on the table until you or the other side makes a new offer."
        : `Withdraw your latest offer${monetary ? ` of ${toUSD(latestLive?.amount)}` : ""}? The previous offer goes back on the table.`,
      onWithdraw
    );
  }
  const onWithdraw = async () => {
    try {
      await withdrawNegotiation(user.token, params.caseId, params.negId);
      snackbar.showSuccess("Offer withdrawn");
    } catch (error) {
      errorHandler(error);
    } finally {
      getNegotiationDetailsData();
    }
  };

  function applyAiAmount(amount) {
    setCounterNeg((prev) => ({ ...prev, amount }));
    setAiDialogOpen(false);
    snackbar.showInfo("Suggested amount added to your counter-offer");
  }

  function applyAiMessage(text) {
    setCounterNeg((prev) => ({ ...prev, text }));
    setAiDialogOpen(false);
    snackbar.showInfo("Draft message added to your counter-offer");
  }

  async function openAiAssistant() {
    setAiDialogOpen(true);
    setAiLoading(true);
    setAiInsight(null);
    try {
      const { data } = await getNegotiationInsight(
        user.token,
        params.caseId,
        params.negId
      );
      setAiInsight(data.data);
    } catch (error) {
      setAiInsight({
        source: "fallback",
        summary: `The current offer is ${toUSD(neg.latest_amount)}.`,
        suggestedNextStep:
          "Review the latest offer and case facts before responding.",
        points: [],
      });
    } finally {
      setAiLoading(false);
    }
  }

  return (
    <>
      <Box p={2} className="flex flex-wrap items-center gap-3">
        <Button onClick={() => navigation(-1)} variant="contained" size="large" color="primary" className="blue-button">
          Go Back
        </Button>
        <FormControl size="small" className="w-full min-[600px]:w-[360px]">
          <InputLabel id="negotiation-detail-case-label">Case</InputLabel>
          <Select
            labelId="negotiation-detail-case-label"
            label="Case"
            value={caseList.some((c) => String(c.id) === String(params.caseId)) ? String(params.caseId) : ""}
            onChange={onCaseChange}
          >
            {caseList.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Paper className="odr-card p-4">
        <Box className="flex flex-wrap items-start justify-between gap-4">
          <Box className="min-w-[240px] flex-1">
            <Typography component="h5" fontWeight={500}>
              {neg.title}
            </Typography>
            <Typography component="p" fontSize="small">
              {neg.description}
            </Typography>
            {neg.terms ? (
              <Typography component="p" fontSize="small" className="!mt-2 whitespace-pre-wrap">
                <b>Other terms:</b> {neg.terms}
              </Typography>
            ) : null}
            {active && latestLive?.items?.length ? (
              <Box className="mt-2">
                <Typography fontSize="small" fontWeight={600}>
                  Terms on the table
                </Typography>
                <TermsList items={latestLive.items} />
              </Box>
            ) : null}
            <Box className="mt-2 flex flex-wrap items-center gap-2">
              {isBlind ? (
                <Chip size="small" variant="outlined" label="Confidential bidding" className="!font-semibold" />
              ) : null}
              {showRound ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Round ${viewer.rounds} of ${viewer.maxRounds}`}
                  className="!font-semibold"
                />
              ) : null}
              {expiry ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={expiry.label}
                  className={`${EXPIRY_CHIP_CLASS[expiry.kind]} !font-semibold`}
                />
              ) : null}
            </Box>
          </Box>
          {active && !isBlind ? (
            <Box className="flex flex-col items-end gap-2">
              <Box display="flex" columnGap={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end" rowGap={1}>
                {monetary ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={openAiAssistant}
                    className="normal-case"
                  >
                    AI Assisted
                  </Button>
                ) : null}
                <Box className="text-right">
                  <Typography fontSize="small" color="text.secondary" lineHeight={1.2}>
                    Current offer
                  </Typography>
                  <Typography fontWeight={600}>{monetary ? toUSD(neg.latest_amount) : "Terms only"}</Typography>
                </Box>
                {viewer?.canAccept || viewer?.canOffer ? (
                  <Button
                    size="large"
                    onClick={onConfirmClick}
                    variant="contained"
                    className="normal-case blue-button"
                    disabled={!canAccept}
                  >
                    Agree & Confirm
                  </Button>
                ) : null}
              </Box>
              {helperText ? (
                <Typography fontSize="small" color="text.secondary" className="max-w-[420px] text-right">
                  {helperText}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>

        {neg.status === "WITHDRAWN" ? (
          <Alert severity="info" className="mt-4">
            {canRestart
              ? "This negotiation was withdrawn. Make a new offer below to restart it."
              : "This negotiation was withdrawn."}
          </Alert>
        ) : null}
        {neg.status === "ENDED" && !isBlind ? (
          <Alert severity="warning" className="mt-4">
            This negotiation has ended without a settlement.
          </Alert>
        ) : null}
        {closed ? (
          <SettlementCard
            settlement={settlement}
            neg={neg}
            onViewAgreement={() =>
              navigation(`/negotiation/${params.caseId}/${params.negId}/agreement`)
            }
          />
        ) : null}
      </Paper>

      <AiNegotiationDialog
        open={aiDialogOpen}
        onClose={() => setAiDialogOpen(false)}
        loading={aiLoading}
        insight={aiInsight}
        onRegenerate={openAiAssistant}
        onUseAmount={canCounter ? applyAiAmount : null}
        onUseMessage={canCounter ? applyAiMessage : null}
      />

      {isBlind ? (
        <BlindBidPanel
          caseId={params.caseId}
          negId={params.negId}
          neg={neg}
          viewer={viewer}
          blind={blind}
          active={active}
          onChanged={getNegotiationDetailsData}
        />
      ) : (
        <>
          {monetary ? <OfferChart entries={entries} viewer={viewer} /> : null}
          <Box
            ref={listRef}
            className="mt-4 max-h-[60vh] overflow-y-auto rounded-xl bg-[#f7f9fc] p-3"
          >
            {entries.map((e) => (
              <OfferBubble entry={e} viewer={viewer} monetary={monetary} key={e.key} />
            ))}
          </Box>
        </>
      )}

      <ProposalPanel
        caseId={params.caseId}
        negId={params.negId}
        viewer={viewer}
        proposals={proposals}
        monetary={monetary}
        onChanged={getNegotiationDetailsData}
      />

      {canCounter || canWithdraw ? (
        <Paper className="mt-4">
          {canCounter ? (
            <Box
              component="form"
              onSubmit={onCounterNegSubmit}
              noValidate
              className="flex flex-wrap items-start gap-2 p-3"
            >
              {monetary ? (
                <TextField
                  type="number"
                  size="small"
                  label="Amount($)"
                  name="amount"
                  value={counterNeg.amount}
                  onChange={onCounterNegChange}
                  inputProps={{ min: 0, step: "any" }}
                  error={Boolean(amountError)}
                  helperText={amountError}
                  className="w-[160px]"
                />
              ) : null}
              <TextField
                size="small"
                label="Say Something"
                name="text"
                value={counterNeg.text}
                onChange={onCounterNegChange}
                inputProps={{ maxLength: 2000 }}
                className="flex-1 min-w-[200px]"
              />
              <TextField
                select
                size="small"
                label="Response time"
                name="response_hours"
                value={counterNeg.response_hours}
                onChange={onCounterNegChange}
                className="w-[140px]"
              >
                {hoursOptions.map((h) => (
                  <MenuItem key={h} value={h}>
                    {responseHoursLabel(h)}
                  </MenuItem>
                ))}
              </TextField>
              <Box className="basis-full">
                <Typography fontSize="small" fontWeight={600} className="!mb-1">
                  Terms{monetary ? " (optional)" : ""}
                </Typography>
                <TermsEditor
                  catalog={catalog}
                  maxItems={maxItems}
                  items={counterItems}
                  onChange={(v) => {
                    itemsDirty.current = true;
                    setCounterItems(v);
                  }}
                />
              </Box>
              <TextField
                multiline
                minRows={2}
                size="small"
                label="Additional terms (optional)"
                name="terms"
                value={counterNeg.terms}
                onChange={onCounterNegChange}
                inputProps={{ maxLength: 2000 }}
                className="basis-full"
              />
              <Box className="flex w-full flex-wrap items-center justify-end gap-2">
                {canWithdraw ? (
                  <Button size="large" color="error" variant="outlined" onClick={onWithdrawClick} className="normal-case">
                    Withdraw my offer
                  </Button>
                ) : null}
                <Button size="large" type="submit" variant="contained" disabled={busy}>
                  Send
                </Button>
              </Box>
            </Box>
          ) : (
            <Box className="flex flex-wrap items-center justify-between gap-2 p-3">
              <Typography fontSize="small" color="text.secondary">
                Your offer is on the table. You can withdraw it until the other side responds.
              </Typography>
              <Button size="large" color="error" variant="outlined" onClick={onWithdrawClick} className="normal-case">
                Withdraw my offer
              </Button>
            </Box>
          )}
        </Paper>
      ) : null}

      {isAdmin && !isBlind ? (
        <AiAdviceLog caseId={params.caseId} negId={params.negId} />
      ) : null}
    </>
  );
}

function SettlementCard({ settlement, neg, onViewAgreement }) {
  const monetary = settlement ? settlement.monetary !== false : neg.monetary !== false;
  const when = settlement?.accepted_at
    ? new Date(settlement.accepted_at).toLocaleString()
    : new Date(neg.updated_at).toLocaleString();
  const rows = [
    ["Offered by", settlement?.offered_by_name],
    ["Accepted by", settlement?.accepted_by_name],
    ["Date & time", when],
  ].filter(([, value]) => value); // confidential bidding has no single offerer / acceptor
  return (
    <Box className="mt-4 rounded-lg border border-solid border-[#a5d6b0] bg-[#f1faf4] p-4">
      <Box className="flex items-center gap-2 text-[#1e7e34]">
        <CheckCircleIcon />
        <Typography fontWeight={600}>Settled</Typography>
      </Box>
      <Typography variant="h4" className="!mt-1 text-[#1e7e34]">
        {monetary ? toUSD(settlement?.amount ?? neg.latest_amount) : "Settled on agreed terms"}
      </Typography>
      {settlement?.terms_items?.length ? <TermsList items={settlement.terms_items} /> : null}
      <Typography className="!mb-2" fontSize="small" color="text.secondary">
        {BASIS_TEXT[settlement?.basis] || BASIS_TEXT.OFFER}
      </Typography>
      <Box className="flex flex-wrap gap-x-10 gap-y-2">
        {rows.map(([label, value]) => (
          <Box key={label}>
            <Typography fontSize="small" color="text.secondary">
              {label}
            </Typography>
            <Typography>{value}</Typography>
          </Box>
        ))}
      </Box>
      {settlement?.terms ? (
        <Box className="mt-3">
          <Typography fontSize="small" color="text.secondary">
            Other terms
          </Typography>
          <Typography className="whitespace-pre-wrap">{settlement.terms}</Typography>
        </Box>
      ) : null}
      {settlement?.closed_case ? (
        <Typography className="!mt-3" fontWeight={600} fontSize="small">
          The case was closed.
        </Typography>
      ) : null}
      <Box className="mt-3">
        <Button
          variant="outlined"
          color="success"
          startIcon={<DescriptionOutlinedIcon />}
          onClick={onViewAgreement}
          className="normal-case"
        >
          View settlement agreement
        </Button>
      </Box>
    </Box>
  );
}

function OfferBubble({ entry: e, viewer, monetary = true }) {
  const style = SIDE_STYLE[e.side] || SIDE_STYLE.NEUTRAL;
  const withdrawn = Boolean(e.withdrawn_at);
  const sideName = style.label && viewer?.side
    ? e.right
      ? `You (${style.label})`
      : style.label
    : null;
  return (
    <Box className={`flex py-1.5 ${e.right ? "justify-end" : "justify-start"}`}>
      <Box
        className={`w-full max-w-[560px] rounded-xl border border-solid p-3 ${style.bubble} ${
          withdrawn ? "opacity-60" : ""
        }`}
      >
        <Box className="flex flex-wrap items-baseline justify-between gap-x-3">
          <Typography fontSize="small" fontWeight={600} className={style.head}>
            {e.creator_name}
            {sideName ? <span className="font-normal"> · {sideName}</span> : null}
          </Typography>
          <Typography fontSize="12px" color="text.secondary">
            {fmtDateTime(e.created_at)}
          </Typography>
        </Box>
        <Box className="flex flex-wrap items-center gap-2">
          {monetary ? (
            <Typography
              variant="h5"
              className={`!font-semibold ${withdrawn ? "line-through text-[#6b7280]" : ""}`}
            >
              {toUSD(e.amount)}
            </Typography>
          ) : null}
          {e.opening ? (
            <Chip size="small" variant="outlined" label="Opening offer" />
          ) : null}
          {withdrawn ? (
            <Chip
              size="small"
              variant="outlined"
              label={`Withdrawn ${fmtDateTime(e.withdrawn_at)}`}
              className="!bg-[#f1f3f5] !text-[#555] !border-[#9aa1ab]"
            />
          ) : null}
        </Box>
        {e.text ? (
          <Typography className="!mt-1 whitespace-pre-wrap">{e.text}</Typography>
        ) : null}
        {e.items?.length ? (
          <Box className={`mt-2 ${withdrawn ? "opacity-70" : ""}`}>
            <TermsList items={e.items} />
          </Box>
        ) : null}
        {e.terms ? (
          <Box className="mt-2 border-0 border-t border-solid border-[#0000001a] pt-1.5">
            <Typography fontSize="12px" fontWeight={600} color="text.secondary">
              Other terms
            </Typography>
            <Typography fontSize="small" className="whitespace-pre-wrap">
              {e.terms}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
